import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { lightThemeClass } from "./light.css.js";
import { darkThemeClass } from "./dark.css.js";
import { themeRoot } from "./ThemeProvider.css.js";
import { vars } from "./contract.css.js";
import { deriveAccentPalette } from "./accent.js";
const DARK_QUERY = "(prefers-color-scheme: dark)";
// Read the OS preference once. Guarded for environments without matchMedia.
const systemPrefersDark = () => typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(DARK_QUERY).matches
    : false;
const ResolvedThemeContext = createContext("light");
// Components that need to branch on the active theme (rare — most use tokens) read it here.
export const useResolvedTheme = () => useContext(ResolvedThemeContext);
const PortalContainerContext = createContext(null);
// vanilla-extract theme tokens resolve at runtime to `var(--hash)` reference strings, not
// the raw custom-property name assignVars() expects — this unwraps one to the other so we
// can override it inline (see `accentStyle` below).
const cssVarName = (ref) => ref.slice(4, -1);
// Where popovers that must escape a transformed ancestor should portal to. It is the
// theme root element, never `document.body`: the theme's CSS custom properties are
// scoped to that element, so a menu portalled to the body resolves every `vars.*` to
// nothing and renders transparent with black text.
export const usePortalContainer = () => useContext(PortalContainerContext);
// Applies the active vanilla-extract theme class at the root. When `mode === 'system'`
// it follows `prefers-color-scheme` live (Doc 10 §6); otherwise it honours the explicit
// choice. Presentational only — the persisted preference lives in `themeStore`.
export const ThemeProvider = ({ mode, children, className, accentColor }) => {
    const [systemDark, setSystemDark] = useState(systemPrefersDark);
    // State, not a ref: portalling consumers must re-render once the node exists.
    const [root, setRoot] = useState(null);
    // Overrides the four accent-derived tokens as inline CSS custom properties, layered on
    // top of whichever light/dark theme class is active below.
    const accentStyle = useMemo(() => {
        if (accentColor === undefined || accentColor === null)
            return undefined;
        const palette = deriveAccentPalette(accentColor);
        return {
            [cssVarName(vars.color.accent)]: palette.accent,
            [cssVarName(vars.color.accentHover)]: palette.accentHover,
            [cssVarName(vars.color.accentSubtle)]: palette.accentSubtle,
            [cssVarName(vars.color.accentText)]: palette.accentText,
        };
    }, [accentColor]);
    useEffect(() => {
        if (mode !== "system")
            return;
        if (typeof window === "undefined" || typeof window.matchMedia !== "function")
            return;
        const mql = window.matchMedia(DARK_QUERY);
        const onChange = (e) => setSystemDark(e.matches);
        setSystemDark(mql.matches);
        mql.addEventListener("change", onChange);
        return () => mql.removeEventListener("change", onChange);
    }, [mode]);
    const resolved = mode === "system" ? (systemDark ? "dark" : "light") : mode;
    const themeClass = resolved === "dark" ? darkThemeClass : lightThemeClass;
    const classes = [themeClass, themeRoot, className].filter(Boolean).join(" ");
    return (_jsx(ResolvedThemeContext.Provider, { value: resolved, children: _jsx("div", { ref: setRoot, className: classes, "data-theme": resolved, style: accentStyle, children: _jsx(PortalContainerContext.Provider, { value: root, children: children }) }) }));
};
//# sourceMappingURL=ThemeProvider.js.map